import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { getAssessments } from '../api/api';
import { PageContainer, Card, Button } from '../components/ui';

const RISK_COLORS = {
  LOW: 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30',
  MEDIUM: 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-yellow-500/15 dark:text-yellow-400 dark:border-yellow-500/30',
  HIGH: 'bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-500/15 dark:text-orange-400 dark:border-orange-500/30',
  CRITICAL: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30',
};

export default function History() {
  const { organizationId } = useParams();
  const { t, language } = useLanguage();
  const locale = (language === 'en-US' || language === 'en') ? 'en-US' : 'pt-BR';
  const RISK_LABELS = { LOW: t.low, MEDIUM: t.medium, HIGH: t.high, CRITICAL: t.critical };
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAssessments(organizationId)
      .then(setAssessments)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [organizationId]);

  return (
    <Layout>
      <PageContainer>
        <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 mb-2">{t.assessmentHistory}</h1>
        <p className="text-[#475569] dark:text-slate-400 mb-8">
          {t.assessmentHistoryDesc}
        </p>

        {error && (
          <div className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30 p-4 rounded-[10px] mb-6">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0ea5e9]" />
          </div>
        ) : assessments.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-[#475569] dark:text-slate-400 mb-4">{t.noAssessmentsFound}</p>
            <Link to={`/org/${organizationId}/assessment`}>
              <Button variant="primary">{t.runAssessment}</Button>
            </Link>
          </Card>
        ) : (
          <Card className="overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left py-4 px-6 text-slate-900 dark:text-slate-400 font-medium">{t.date}</th>
                <th className="text-left py-4 px-6 text-slate-900 dark:text-slate-400 font-medium">{t.score}</th>
                <th className="text-left py-4 px-6 text-slate-900 dark:text-slate-400 font-medium">{t.riskLevel}</th>
                <th className="text-left py-4 px-6 text-slate-900 dark:text-slate-400 font-medium">{t.vulnerabilities}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((a) => (
                <tr key={a.id} className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                  <td className="py-4 px-6 text-slate-700 dark:text-slate-300">
                    {new Date(a.createdAt).toLocaleString(locale)}
                  </td>
                  <td className="py-4 px-6 font-medium text-slate-700 dark:text-slate-300">{a.score}</td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${RISK_COLORS[a.riskLevel] || 'bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-500/15 dark:text-slate-400 dark:border-slate-500/30'}`}>
                      {RISK_LABELS[a.riskLevel] || a.riskLevel}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-slate-700 dark:text-slate-300">{a.vulnerabilities?.length ?? 0}</td>
                  <td className="py-4 px-6">
                    <Link
                      to={`/org/${organizationId}/report/${a.id}`}
                      className="text-[#0ea5e9] hover:text-[#0284c7] font-medium transition duration-200"
                    >
                      {t.viewReport}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </Card>
        )}
      </PageContainer>
    </Layout>
  );
}
